export const VAR_CLASS = "bbj-var";
export const VAR_FIELD_CLASS = "bbj-var-field";
export const VAR_RAW_CLASS = "bbj-var-raw";
export const VAR_DATA_ATTR = "data-bbj-var";

/**
 * Escapes special regex characters within an arbitrary string so it can be safely
 * interpolated inside a RegExp constructor.
 */
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Matches a simple Handlebars-like placeholder: {{variable}}
 * Example match: {{userName}}
 */
export const buildSimpleVariablePlaceholderRegex = (variableValue: string): RegExp =>
  new RegExp(`(?<!\\{)\\{\\{${escapeRegExp(variableValue)}\\}\\}(?!\\})`, "g");

export const buildRawVariablePlaceholderRegex = (variableValue: string): RegExp =>
  new RegExp(`\\{\\{\\{${escapeRegExp(variableValue)}\\}\\}\\}`, "g");

/**
 * Matches a Handlebars-like each block for a given list name, non-greedily capturing inner content.
 * Example match: {{#each items}} ... {{/each}}
 */
export const buildListEachBlockRegex = (listValue: string): RegExp =>
  new RegExp(`\\{\\{#each\\s+${escapeRegExp(listValue)}\\}\\}([\\s\\S]*?)\\{\\{\\/each\\}\\}`, "g");

/**
 * Matches a rendered simple variable span created by the editor for a given variable name.
 * Example match: <span class="... bbj-var ..." data-bbj-var="userName">[Label]</span>
 */
export const buildSimpleVariableSpanRegex = (variableValue: string): RegExp =>
  new RegExp(
    `<span[^>]*class="[^"]*${escapeRegExp(VAR_CLASS)}[^"]*"[^>]*${VAR_DATA_ATTR}="${escapeRegExp(variableValue)}"[^>]*>[^<]*<\\/span>`,
    "g"
  );

export const buildRawVariableSpanRegex = (variableValue: string): RegExp =>
  new RegExp(
    `<span[^>]*class="[^"]*${escapeRegExp(VAR_RAW_CLASS)}[^"]*"[^>]*${VAR_DATA_ATTR}="${escapeRegExp(variableValue)}"[^>]*>[^<]*<\\/span>`,
    "g"
  );

/**
 * Matches a rendered list block delimited by start and end spans for a given list name,
 * capturing the inner HTML in group 1.
 * Example match: <span data-bbj-var="items">...</span>(...)<span data-bbj-var="items-end">...</span>
 */
export const buildListBlockSpanRegex = (listValue: string): RegExp =>
  new RegExp(
    `<span[^>]*${VAR_DATA_ATTR}="${escapeRegExp(listValue)}"[^>]*>[^<]*<\\/span>([\\s\\S]*?)<span[^>]*${VAR_DATA_ATTR}="${escapeRegExp(listValue)}-end"[^>]*>[^<]*<\\/span>`,
    "g"
  );

/**
 * Matches a rendered list field span for a given field name within a list block.
 * Example match: <span class="... bbj-var-field ..." data-bbj-var="field">[Label]</span>
 */
export const buildListFieldSpanRegex = (fieldValue: string): RegExp =>
  new RegExp(
    `<span[^>]*class="[^"]*${escapeRegExp(VAR_FIELD_CLASS)}[^"]*"[^>]*${VAR_DATA_ATTR}="${escapeRegExp(fieldValue)}"[^>]*>[^<]*<\\/span>`,
    "g"
  );

/**
 * Matches <br>, <br/>, and <br /> variants, optionally followed by a newline; used to normalize to \n.
 */
export const BR_TAG_VARIANTS_REGEX = /<br\s*\/?>\r?\n?/g;

/**
 * Matches any <span>...</span> wrapper (non-greedy inner capture), used to strip wrappers while preserving inner text.
 */
export const GENERIC_SPAN_WRAPPER_REGEX = /<span[^>]*>([\s\S]*?)<\/span>/g;

/**
 * Matches a generic Handlebars-like each block start, regardless of list name.
 * Example match: {{#each items}}
 */
export const LIST_EACH_GENERIC_START_REGEX = /\{\{#each\s+\w+\}\}/g;

/**
 * Matches a Handlebars-like each block end.
 * Example match: {{\/each}}
 */
export const LIST_EACH_GENERIC_END_REGEX = /\{\{\/each\}\}/g;

/**
 * Matches a bracket-formatted variable label: [Label]
 * But only when NOT already inside a span element
 * Example match: [Prénom candidat] (but not inside <span>...</span>)
 */
export const buildSimpleLabelRegex = (label: string): RegExp =>
  new RegExp(`(?<!<span[^>]*>)(?<!\\[)\\[${escapeRegExp(label)}\\](?!\\])`, "gi");

export const buildRawLabelRegex = (label: string): RegExp =>
  new RegExp(`(?<!<span[^>]*>)\\[\\[${escapeRegExp(label)}\\]\\]`, "gi");
